'use client';

import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

import { Input, type InputProps } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  required?: boolean;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

type FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ControllerProps<TFieldValues, TName> & {
  required?: boolean;
};

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  required,
  ...props
}: FormFieldProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name, required }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    required: fieldContext.required,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

function FormItem({ className, ...props }: React.ComponentProps<'div'>) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div data-slot='form-item' className={cn('grid gap-4', className)} {...props} />
    </FormItemContext.Provider>
  );
}

function FormLabel({ className, children, ...props }: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId, required } = useFormField();

  return (
    <Label
      data-slot='form-label'
      data-error={!!error}
      className={cn('data-[error=true]:text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    >
      {children}
      {required && <span className='text-swd-tuerkis'>*</span>}
    </Label>
  );
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      data-slot='form-control'
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot='form-description'
      id={formDescriptionId}
      className={cn('text-body-2 text-muted-foreground', className)}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }: React.ComponentProps<'p'>) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : props.children;

  return (
    <p
      data-slot='form-message'
      id={formMessageId}
      className={cn('input-error-message min-h-5 -mt-2', !body && 'invisible', className)}
      {...props}
    >
      {body || '\u00A0'}
    </p>
  );
}

// Simplified FormInput component that handles all form logic internally
interface FormInputProps extends InputProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  containerClassName?: string;
}

function FormInput({
  className,
  state,
  label,
  description,
  containerClassName,
  size = 'small',
  ...props
}: FormInputProps) {
  const { error, formItemId, formDescriptionId, formMessageId, required } = useFormField();

  // Determine the effective state based on error
  const effectiveState = error ? 'error' : state;

  return (
    <div className={cn('grid', size === 'small' ? 'gap-2' : 'gap-4', containerClassName)}>
      {label && (
        <Label
          data-slot='form-label'
          data-error={!!error}
          className={cn('data-[error=true]:text-destructive')}
          htmlFor={formItemId}
        >
          {label}
          {required && <span className='text-swd-tuerkis'>*</span>}
        </Label>
      )}

      <Input
        id={formItemId}
        className={className}
        state={effectiveState}
        size={size}
        aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
        aria-invalid={!!error}
        {...props}
      />

      {description && !error && (
        <p data-slot='form-description' id={formDescriptionId} className='text-body-2 text-muted-foreground'>
          {description}
        </p>
      )}

      <p
        data-slot='form-message'
        id={formMessageId}
        className={cn('input-error-message min-h-5 -mt-2', !error && 'invisible')}
      >
        {error ? error.message : '\u00A0'}
      </p>
    </div>
  );
}

export { Form, FormControl, FormDescription, FormField, FormInput, FormItem, FormLabel, FormMessage, useFormField };
